import type { WebhookEndpoint } from '@compass/client/types/webhook';
import { WebhookEndpointStatus } from '@compass/client/types/webhook';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { EndpointStatusAction } from '../hooks/useWebhookEndpoints';
import { DELETE_CONFIRM_WORD, isTypedConfirmation } from '../utils';

interface Copy {
  title: string;
  body: string;
  confirm: string;
  destructive: boolean;
}

function copyFor(action: EndpointStatusAction, endpoint: WebhookEndpoint): Copy {
  switch (action) {
    case 'pause':
      return {
        title: 'Pause endpoint',
        body: 'New events are recorded but not sent, and pending retries are cancelled. When you resume, everything missed while paused is replayed.',
        confirm: 'Pause',
        destructive: false,
      };
    case 'resume':
      return {
        title: 'Resume endpoint',
        body:
          endpoint.status === WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_PAUSED
            ? 'Deliveries start again, and events missed while the endpoint was paused are replayed.'
            : 'Deliveries start again for new events. Events that occurred while it was disabled are not sent.',
        confirm: 'Resume',
        destructive: false,
      };
    case 'disable':
      return {
        title: 'Disable endpoint',
        body: 'No events are recorded or sent, and pending retries are cancelled. Events that occur while it is disabled are not replayed on resume.',
        confirm: 'Disable',
        destructive: true,
      };
    case 'delete':
      return {
        title: 'Delete endpoint',
        body: 'The endpoint, its signing secrets and custom headers are removed, and pending deliveries are cancelled. This cannot be undone.',
        confirm: 'Delete',
        destructive: true,
      };
  }
}

interface ConfirmEndpointActionDialogProps {
  action: EndpointStatusAction;
  endpoint: WebhookEndpoint;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirms pause / resume / disable / delete. Delete also needs a typed DELETE. */
export function ConfirmEndpointActionDialog({
  action,
  endpoint,
  isPending,
  onConfirm,
  onClose,
}: ConfirmEndpointActionDialogProps) {
  const [typed, setTyped] = useState('');
  const copy = copyFor(action, endpoint);
  const needsTyping = action === 'delete';
  const canConfirm =
    !isPending && (!needsTyping || isTypedConfirmation(typed, DELETE_CONFIRM_WORD));

  return (
    <Dialog
      open
      onOpenChange={next => {
        if (!next && !isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{endpoint.name || 'This endpoint'}</span>
            {endpoint.url_host ? ` (${endpoint.url_host})` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">{copy.body}</p>
          {needsTyping && (
            <div className="space-y-2">
              <Label htmlFor="confirm-action">
                Type{' '}
                <span className="font-mono font-medium text-foreground">{DELETE_CONFIRM_WORD}</span>{' '}
                to confirm
              </Label>
              <Input
                id="confirm-action"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={DELETE_CONFIRM_WORD}
                autoComplete="off"
                autoFocus
                disabled={isPending}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={copy.destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={!canConfirm}
            data-analytics-id={`webhooks:endpoint:${action}`}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
